import { useIntersectionObserver } from '@vueuse/core';
import { computed, ref, type Ref } from 'vue';

interface UseBatchLoaderOptions<TRef, TItem> {
  batchSize: number;
  loadGeneration: Ref<number>;
  fetchBatch: (refs: TRef[]) => Promise<(TItem | null)[]>;
}

export function useBatchLoader<TRef, TItem>(options: UseBatchLoaderOptions<TRef, TItem>) {
  const items = ref<TItem[]>([]) as Ref<TItem[]>;
  const allRefs = ref<TRef[]>([]) as Ref<TRef[]>;
  const loadedCount = ref(0);
  const isLoading = ref(false);
  const isLoadingMore = ref(false);
  const hasMore = computed(() => loadedCount.value < allRefs.value.length);
  const sentinel = ref<HTMLElement>();

  function reset() {
    allRefs.value = [];
    items.value = [];
    loadedCount.value = 0;
    isLoading.value = false;
    isLoadingMore.value = false;
  }

  async function refresh(newRefs: TRef[]) {
    allRefs.value = newRefs;

    if (newRefs.length === 0) {
      items.value = [];
      loadedCount.value = 0;
      return;
    }

    const gen = options.loadGeneration.value;
    const firstBatch = newRefs.slice(0, options.batchSize);
    const results = await options.fetchBatch(firstBatch);

    if (gen !== options.loadGeneration.value) return;

    items.value = results.filter((r): r is TItem => r !== null);
    loadedCount.value = firstBatch.length;
  }

  async function loadNext() {
    if (isLoadingMore.value || !hasMore.value) return;
    isLoadingMore.value = true;
    const gen = options.loadGeneration.value;

    const nextRefs = allRefs.value.slice(loadedCount.value, loadedCount.value + options.batchSize);
    const results = await options.fetchBatch(nextRefs);

    if (gen !== options.loadGeneration.value) return;

    const newItems = results.filter((r): r is TItem => r !== null);
    items.value = [...items.value, ...newItems];
    loadedCount.value += nextRefs.length;
    isLoadingMore.value = false;
  }

  const { stop } = useIntersectionObserver(sentinel, ([entry]) => {
    if (entry.isIntersecting && hasMore.value) {
      loadNext();
    }
  });

  return { items, allRefs, isLoading, hasMore, sentinel, loadNext, reset, refresh, stop };
}
